
/* ============================================================
   MOTION — every animation comes from motion.dev (vanilla).
   Tokens read live from the framework's CSS custom properties.
   ============================================================ */
import { animate, scroll, inView, stagger, hover, press } from "https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm";
import { labColorHref } from "/src/figs-nav/figs-nav.js";

const CS = getComputedStyle(document.documentElement);
const raw = (n) => CS.getPropertyValue(n).trim();
const sec = (n) => { const v = raw(n); return v.endsWith("ms") ? parseFloat(v)/1000 : parseFloat(v); };
const px  = (n) => parseFloat(raw(n));
const num = (n) => parseFloat(raw(n));
const ease = (n) => { const m = raw(n).match(/-?[\d.]+/g); return m && m.length===4 ? m.map(Number) : "easeOut"; };

const MO = {
  instant:sec("--mo-instant"), quick:sec("--mo-quick"), base:sec("--mo-base"),
  slow:sec("--mo-slow"), deliberate:sec("--mo-deliberate"), cinematic:sec("--mo-cinematic"),
  eStandard:ease("--mo-ease-standard"), eEntrance:ease("--mo-ease-entrance"),
  eExit:ease("--mo-ease-exit"), eEmphasis:ease("--mo-ease-emphasis"),
  riseXs:px("--mo-rise-xs"), riseSm:px("--mo-rise-sm"), riseMd:px("--mo-rise-md"), riseLg:px("--mo-rise-lg"),
  stagTight:sec("--mo-stagger-tight"), stagBase:sec("--mo-stagger-base"), stagLoose:sec("--mo-stagger-loose"),
  pNear:num("--mo-parallax-near"), pMid:num("--mo-parallax-mid"), pFar:num("--mo-parallax-far"),
  scaleHover:num("--mo-scale-hover"), scaleIn:num("--mo-scale-in"), scalePress:num("--mo-scale-press"),
};

const $  = (s,r=document) => r.querySelector(s);
const $$ = (s,r=document) => [...r.querySelectorAll(s)];
const mqDesktop = matchMedia("(min-width:1024px)");

let reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
let speed = 1;
const tracked = new Set();
const teardown = [];

function track(c){
  if(!c || typeof c!=="object") return c;
  if(!("speed" in c) && typeof c.stop!=="function") return c;
  tracked.add(c); try{ c.speed = speed; }catch(e){}
  Promise.resolve(c.finished ?? c).then(()=>tracked.delete(c)).catch(()=>tracked.delete(c));
  return c;
}
function tween(el, kf, opts={}){
  if(!el || (Array.isArray(el) && !el.length)) return;
  if(reduced){
    const flat={};
    for(const [k,v] of Object.entries(kf)) flat[k] = (k==="opacity"||k==="clipPath") ? v : (Array.isArray(v)? v[v.length-1] : v);
    return track(animate(el, flat, {duration:MO.instant, delay:0, ease:"linear"}));
  }
  return track(animate(el, kf, opts));
}

/* ============================================================
   DATA — products sourced live from wearfigs.com PDPs.
   Swatch hexes are the site's own ColorSwatch values.
   ============================================================ */
/* Names/prices sourced from wearfigs.com (2026-10-06) to match each photo's
   actual garment, not the old generic "HW Livingston" placeholder:
   - Dark Harbor / Charcoal / Caribbean Blue: the smooth fitted zip jacket
     photographed here is the Sydney Scrub Jacket, $98 — the one women's
     outerwear style FIGS currently offers across all three colorways.
   - Burgundy: the sleeveless layer is the Sydney Scrub Vest, $88 (same
     line as the jacket, different silhouette).
   - Black: the fuzzy teddy-texture zip-up is the High-Pile Fleece Bomber
     Jacket, $128 (currently shown in Caribbean Blue on-site, but this is
     the correct style/price regardless of which colorway is in stock). */
const PRODUCTS = {
  picksForYou:[
    {name:"Sydney Scrub Jacket",price:"$98.00",colorName:"Dark Harbor",image:"assets/figma/products/hw-livingston-dark-harbor.png",url:"#"},
    {name:"Sydney Scrub Vest",price:"$88.00",colorName:"Burgundy",image:"assets/figma/products/hw-livingston-burgundy.png",url:"#"},
    {name:"High-Pile Fleece Bomber Jacket",price:"$128.00",colorName:"Black",image:"assets/figma/products/hw-livingston-black.png",url:"#"},
    {name:"Sydney Scrub Jacket",price:"$98.00",colorName:"Charcoal",image:"assets/figma/products/hw-livingston-charcoal.png",url:"#"},
    {name:"Sydney Scrub Jacket",price:"$98.00",colorName:"Caribbean Blue",image:"assets/figma/products/hw-livingston-caribbean-blue.png",url:"#"}
  ]
};

/* Shop-the-look — the garment(s) the Color Lab model wears, mapped to the
   upper (top) and lower (pant) hotspot, per gender. wearfigs.com PDP data.
   Women wears the real Rafaela jumpsuit — a single SKU, so there's only
   one thing to cross-sell and only the `top` hotspot is defined; `pant`
   is intentionally absent and paintHotcards()/positionHotspots() hide
   that dot+card whenever LOOK[gender][key] comes back undefined. `at`
   coordinates are fractions of the photo (not the rendered box) — each
   women's colour carries its own `hot` (the jumpsuit's chest pocket, the
   models differ shot to shot), and positionHotspots() places either one
   through wherever the photo lands on screen. */
const LOOK = {
  women:{
    top:{ kicker:"Shop the jumpsuit", name:"Rafaela Wide-Leg ScrubJumpsuit™", price:"$108",
          url:"https://www.wearfigs.com/collections/wide-leg-jumpsuits-womens/products/womens-rafaela-cargo-wide-leg-scrub-jumpsuit?color=Black",
          image:"https://cdn.shopify.com/s/files/1/0139/8942/files/Q3_2024_9_BLACK_WIDELEGRAFAELAJUMPSUIT_MELANIE_946_55497ac9-24f7-456b-bc67-cd5250e4787a.jpg?v=1775675481&width=200",
          at:{x:.56,y:.25} }   // chest pocket (fallback — each colour has its own `hot`)
  },
  men:{
    top:{ kicker:"Shop the top", name:"Leon Scrub Top™", price:"$42",
          url:"https://www.wearfigs.com/products/mens-leon-scrub-top",
          image:"https://cdn.shopify.com/s/files/1/0139/8942/files/Q3_2024_9_BLACK_LEON_SCOTT_00068.jpg?v=1775238179&width=200",
          at:{x:.555,y:.24} },   // chest — on a centred figure (centre() lines each photo up)
    pant:{ kicker:"Shop the pants", name:"Tansen Jogger Scrub Pant™", price:"$52",
          url:"https://www.wearfigs.com/products/mens-tansen-scrub-pants",
          image:"https://cdn.shopify.com/s/files/1/0139/8942/files/Q3_2023_08_BLACK_TANSEN_CORE-REFRESH_M_SCOTT_24484_e365af79-66dc-4408-ad61-87e3dbe37f7f.jpg?v=1773870224&width=200",
          at:{x:.445,y:.64} }   // thigh
  }
};

/* Colour Lab — each gender has its own 20-colour list in the wheel's
   order. The men's (below) is the colours Leon and Rafaela share; the
   women's is the 20 jumpsuit colours the design team shot, which swap
   F*ing Pink and Hibiscus for Mauve and Sienna. setGender() keeps the
   named colour across the toggle where both have it (18 of 20), else
   falls back to Black. */
const COLOR_LIST = [
  ["01","000000","Black","Core"],           ["02","403f6f","Navy","Core"],
  ["03","8d8d97","Graphite","Core"],        ["04","5e7ec3","Ceil Blue","Core"],
  ["05","095bdb","Royal Blue","Core"],      ["06","692a2d","Burgundy","Core"],
  ["07","645b67","Charcoal","Core"],        ["08","56584c","Moss","Core"],
  ["09","24323f","Dark Harbor","Core"],     ["10","18455a","Caribbean Blue","Core"],
  ["11","f7b1c8","Fight Club Pink","Limited"],["12","cb5b8a","F*ing Pink","Limited"],
  ["13","954955","Berry Spice","Limited"],  ["14","007ab0","Blue Horizon","Limited"],
  ["15","c2bf74","Celery","Limited"],       ["16","462639","Deep Purple","Limited"],
  ["17","84949f","Harbor Grey","Limited"],  ["18","d93744","Hibiscus","Limited"],
  ["19","756539","Martini Olive","Limited"],["20","bad5c0","Sagewater","Limited"],
];

/* Women — the Rafaela Wide-Leg ScrubJumpsuit™ as the design team exported
   it (Dropbox: 2026 SITE FPO ASSETS › Homepage › JUMPSUITS › Exports,
   2026-10-06): transparent 440×1280 PNGs, used byte for byte — no
   background removal, compositing or filters — plus a 110×320 thumb for
   the rail. The same file serves mobile (it's already small). `hot` is
   the chest pocket on each colour's model, as fractions of the canvas. */
const WOMEN_LIST = [
  ["01","000000","Black","Core",.544,.248],        ["02","403f6f","Navy","Core",.548,.253],
  ["03","8d8d97","Graphite","Core",.542,.245],     ["04","5e7ec3","Ceil Blue","Core",.533,.245],
  ["05","095bdb","Royal Blue","Core",.519,.246],   ["06","692a2d","Burgundy","Core",.547,.246],
  ["07","885157","Mauve","Core",.542,.244],        ["08","645b67","Charcoal","Core",.538,.245],
  ["09","56584c","Moss","Core",.665,.246],         ["10","24323f","Dark Harbor","Core",.61,.246],
  ["11","18455a","Caribbean Blue","Core",.556,.247],["12","f7b1c8","Fight Club Pink","Limited",.585,.254],
  ["13","954955","Berry Spice","Limited",.616,.248],["14","007ab0","Blue Horizon","Limited",.593,.246],
  ["15","c2bf74","Celery","Limited",.532,.243],    ["16","462639","Deep Purple","Limited",.599,.253],
  ["17","84949f","Harbor Grey","Limited",.631,.245],["18","756539","Martini Olive","Limited",.62,.253],
  ["19","bad5c0","Sagewater","Limited",.612,.249], ["20","90403b","Sienna","Limited",.577,.247],
];
const WOMEN_COLORS = WOMEN_LIST.map(([i,hex,name,cat,hx,hy])=>({
  i, hex:"#"+hex, name, cat, fit:"contain", hot:{x:hx,y:hy},
  full:`assets/color-lab-women/${i}-${hex}.png`,
  sm:`assets/color-lab-women/${i}-${hex}.png`,
  thumb:`assets/color-lab-women/${i}-${hex}@thumb.png`,
}));

/* Men — real FIGS photography: the Leon Scrub Top™
   (wearfigs.com/collections/core-scrub-tops-mens/products/mens-leon-scrub-
   top), each colour's full-body/2nd gallery image (a few older colourways
   only have a waist-up "hero" shot in their gallery — used as-is rather
   than dropping the colour) sourced live on 2026-09-29, background-removed
   and composited onto solid white with the exact same pipeline as Women's
   Rafaela set, at the same three sizes. Replaces the old 23-swatch WebP
   cutout placeholder set. `cx` is where Leon stands in the few photos shot
   off-centre (a fraction of the photo's width, measured at his torso):
   centre() shifts those so he lands mid-frame; the files stay as sourced. */
const MEN_CX = { "01":.387, "06":.395, "13":.482, "18":.47 };
const MEN_COLORS = COLOR_LIST.map(([i,hex,name,cat])=>({
  i, hex:"#"+hex, name, cat, cx:MEN_CX[i],
  full:`assets/color-lab-men/${i}-${hex}.webp`,
  sm:`assets/color-lab-men/${i}-${hex}@sm.webp`,
  thumb:`assets/color-lab-men/${i}-${hex}@thumb.webp`,
}));

/* 0 · SCROLL PROGRESS — hardware-accelerated */
scroll(animate($("[data-progress]"), {scaleX:[0,1]}, {ease:"linear"}));

/* 2 · HERO — one plate fading up as a single move (Moncler).
   No panel wipe, no scroll cue. The plate carries a slow settle
   that outlasts the copy, so nothing arrives in lockstep. */
function initHero(){
  const hero=$("[data-hero]"), media=$("[data-hero-media]"), content=$("[data-hero-content]");
  const lines=$$("[data-hero-line] > span"), stack=$$("[data-hero-stack]");

  /* no opacity here on purpose — see the .mo-pre note; the plate settles
     in from a transform only, so the <video> is visible from frame one */
  tween(media,{y:[MO.riseLg*.55,0],scale:[1.055,1]},
    {duration:MO.cinematic*1.45, ease:MO.eEntrance});
  tween(lines,{y:["140%","0%"]},
    {duration:MO.deliberate, delay:stagger(MO.stagBase,{startDelay:.42}), ease:MO.eEntrance});
  tween(stack,{y:[MO.riseSm,0],opacity:[0,1]},
    {duration:MO.slow, delay:stagger(MO.stagTight,{startDelay:.66}), ease:MO.eEntrance});

  /* cached — reading offsetHeight inside the scroll callback forced a
     synchronous layout every frame, which was its own source of jank */
  let heroH=hero.offsetHeight;
  const h=()=>heroH;

  /* The copy block used to be lifted by a per-frame JS transform; that
     always trailed the compositor by a frame and read as jitter. It is
     pure CSS sticky now — see .hero__body-content. */
  const lockBlock=$("[data-hero-lock]");
  if(lockBlock) lockBlock.style.transform="";   // clear any stale inline lift

  /* Copy fade ---------------------------------------------------------
     p is the fraction of the hero scrolled past (range is start/start →
     end/start), so the hero is (1 - p) visible. The old curve, 1 - p*1.6,
     was fully transparent at p=0.625 — i.e. with 37.5% of the banner still
     on screen. Hold full opacity instead, then fade across the final
     stretch so it reaches 0 at p=0.90, with ~10% of the hero left in view. */
  const FADE_FROM=0.72, FADE_TO=0.90;
  const fadeAt=(p)=>1-Math.min(1,Math.max(0,(p-FADE_FROM)/(FADE_TO-FADE_FROM)));

  /* The copy block itself no longer drifts up as it goes — it holds its
     position and only the opacity changes. (Cleared explicitly so a stale
     inline transform from a previous boot can't survive a Replay.) */
  content.style.transform="";

  teardown.push(scroll((p)=>{
    media.style.transform=`translate3d(0,${p*h()*MO.pFar}px,0)`;
    content.style.opacity=String(fadeAt(p));
  },{target:hero,offset:["start start","end start"]}));

  const onResize=()=>{ heroH=hero.offsetHeight; };
  addEventListener("resize",onResize,{passive:true});
  teardown.push(()=>removeEventListener("resize",onResize));
}

/* 2b · VIDEO — real play/pause, plus autoplay only while in view
   so an off-screen hero isn't burning decode. */
function initVideo(){
  const vids=$$("[data-video]");
  /* poster can't be media-switched from markup the way <source> can */
  const heroV=$("[data-hero-video]");
  if(heroV && matchMedia("(max-width:767px)").matches) heroV.poster="assets/video/hero-mobile.webp";

  vids.forEach(v=>{
    v.muted=true; v.playsInline=true;      // both required for mobile autoplay
    let inview=false, timer=null;
    const want=()=> inview && !v.dataset.userPaused;
    const tryPlay=()=>{
      if(!want() || !v.paused) return;
      const pr=v.play(); if(pr && pr.catch) pr.catch(()=>{});
    };
    /* A refused play() never retries itself. WebKit refuses muted autoplay
       while the element is still transparent — which it briefly is, because
       the entrance pre-hides [data-hero-media]. So nudge it until it really
       starts, bounded to ~6s, and stop the moment it is playing. */
    const nudge=(n=0)=>{
      clearTimeout(timer);
      if(!want() || !v.paused || n>24) return;
      tryPlay();
      timer=setTimeout(()=>nudge(n+1),250);
    };
    ["loadeddata","canplay","canplaythrough"].forEach(ev=>v.addEventListener(ev,()=>nudge()));
    /* last resort: the first user gesture always satisfies the policy */
    const onGesture=()=>tryPlay();
    addEventListener("pointerdown",onGesture,{passive:true});
    addEventListener("touchstart",onGesture,{passive:true});
    teardown.push(()=>{
      clearTimeout(timer);
      removeEventListener("pointerdown",onGesture);
      removeEventListener("touchstart",onGesture);
    });
    /* pause only once fully out of view, resume on any re-entry */
    teardown.push(inView(v,()=>{
      inview=true; nudge();
      return ()=>{ inview=false; clearTimeout(timer); if(!v.paused) v.pause(); };
    },{amount:"some"}));
  });

  $$("[data-video-toggle]").forEach(btn=>{
    const scope = btn.closest("section");
    const v = $("[data-video]", scope);
    if(!v){ btn.remove(); return; }
    const icon = $("svg", btn);
    const paint = () => {
      const playing = !v.paused;
      btn.setAttribute("aria-label", playing ? "Pause background video" : "Play background video");
      btn.setAttribute("aria-pressed", String(!playing));
      icon.innerHTML = playing
        ? '<path d="M0 0h3v12H0zM7 0h3v12H7z"/>'
        : '<path d="M0 0l10 6-10 6z"/>';
      icon.setAttribute("viewBox", playing ? "0 0 10 12" : "0 0 10 12");
    };
    btn.addEventListener("click", ()=>{
      if(v.paused){ v.dataset.userPaused=""; delete v.dataset.userPaused; v.play().catch(()=>{}); }
      else { v.pause(); v.dataset.userPaused="1"; }
      track(animate(icon,{opacity:[0,1],scale:[.72,1]},
        reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eEntrance}));
    });
    v.addEventListener("play",paint); v.addEventListener("pause",paint);
    teardown.push(press(btn,()=>{
      track(animate(btn,{scale:MO.scalePress},{duration:MO.instant,ease:MO.eStandard}));
      return ()=>track(animate(btn,{scale:1},reduced?{duration:MO.instant}:{type:"spring",stiffness:520,damping:16}));
    }));
    paint();
  });
}

/* 3 · COLLECTIONS */
function initCollections(){
  const tiles=$$("[data-ctile]");
  teardown.push(inView($("[data-ctile-rail]"),()=>{
    tween(tiles,{y:[MO.riseMd,0],opacity:[0,1],clipPath:["inset(0 0 12% 0)","inset(0 0 0% 0)"]},
      {duration:MO.deliberate,delay:stagger(MO.stagBase),ease:MO.eEntrance});
  },{amount:.15}));
  tiles.forEach(t=>{
    const plate=$("[data-ctile-plate]",t), label=$(".ctile__label",t);
    teardown.push(hover(t,()=>{
      track(animate(plate,{scale:MO.scaleIn},{duration:MO.slow,ease:MO.eEntrance}));
      track(animate(label,{y:-4},{duration:MO.base,ease:MO.eEntrance}));
      return ()=>{ track(animate(plate,{scale:1},{duration:MO.slow,ease:MO.eEntrance}));
                   track(animate(label,{y:0},{duration:MO.base,ease:MO.eEntrance})); };
    }));
  });
}

/* 4 · BANNER */
function initBanner(){
  const banner=$("[data-banner]"), media=$("[data-banner-media]");
  const line=$("[data-banner-line] > span"), items=$$("[data-banner-item]");
  teardown.push(inView(banner,()=>{
    tween(line,{y:["140%","0%"]},{duration:MO.deliberate,ease:MO.eEntrance});
    tween(items,{y:[MO.riseSm,0],opacity:[0,1]},{duration:MO.slow,delay:stagger(MO.stagBase,{startDelay:.12}),ease:MO.eEntrance});
  },{amount:.3}));
  /* No parallax on the full banner: the transform on the media layer kept
     re-crossing the IntersectionObserver threshold and pausing the
     background video. The plate now sits still and the video just plays. */
  media.style.transform="";
}

/* 5 · SPLIT CARDS */
function initSplit(){
  $$("[data-split-card]").forEach((card,i)=>{
    const plate=$("[data-split-plate]",card);
    const line=$("[data-split-line]",card), items=$$("[data-split-item]",card);
    teardown.push(inView(card,()=>{
      tween(card,{y:[MO.riseLg,0],opacity:[0,1],clipPath:["inset(0 0 8% 0)","inset(0 0 0% 0)"]},
        {duration:MO.cinematic,delay:i%2===0?0:MO.stagLoose,ease:MO.eEntrance});
      if(line) tween(line,{y:[MO.riseSm,0],opacity:[0,1]},{duration:MO.deliberate,delay:.24,ease:MO.eEntrance});
      if(items.length) tween(items,{y:[MO.riseSm,0],opacity:[0,1]},
        {duration:MO.slow,delay:stagger(MO.stagBase,{startDelay:.4}),ease:MO.eEntrance});
    },{amount:.25}));
    teardown.push(hover(card,()=>{
      track(animate(plate,{scale:MO.scaleIn},{duration:MO.deliberate,ease:MO.eEntrance}));
      return ()=>track(animate(plate,{scale:1},{duration:MO.deliberate,ease:MO.eEntrance}));
    }));
    if(!reduced) teardown.push(scroll((p)=>{
      plate.style.transform=`translate3d(0,${(p-.5)*-card.offsetHeight*MO.pNear}px,0) scale(1.06)`;
    },{target:card,offset:["start end","end start"]}));
  });
}

/* 5b · GIF PLAY/PAUSE — browsers give an animated <img> no native pause,
   so this hand-builds one for WCAG 2.2.2 (Pause, Stop, Hide): freeze the
   frame that's on screen onto a canvas the instant you pause, and stop the
   <img> decoding underneath it. Resuming restarts the GIF from frame one —
   an unavoidable limit of the format, not a bug. */
function initGifControls(){
  $$("[data-gif-toggle]").forEach(btn=>{
    const scope=btn.closest(".scard")||btn.parentElement;
    const img=$("[data-gif-plate]",scope), canvas=$("[data-gif-freeze]",scope);
    if(!img || !canvas) return;
    const ctx=canvas.getContext("2d");
    const originalSrc=img.getAttribute("src");
    const icon=$("svg",btn);
    let frozen=false;

    function paint(){
      const r=img.getBoundingClientRect();
      const dpr=Math.min(2,window.devicePixelRatio||1);
      canvas.width=Math.max(1,Math.round(r.width*dpr));
      canvas.height=Math.max(1,Math.round(r.height*dpr));
      const iw=img.naturalWidth, ih=img.naturalHeight;
      if(!iw || !ih){ try{ ctx.drawImage(img,0,0,canvas.width,canvas.height); }catch(e){} return; }
      // match the <img>'s object-fit:cover crop instead of stretching the
      // whole source into the canvas box — the plate and the gif rarely
      // share an aspect ratio.
      const boxRatio=canvas.width/canvas.height, imgRatio=iw/ih;
      let sx=0, sy=0, sw=iw, sh=ih;
      if(imgRatio>boxRatio){ sw=ih*boxRatio; sx=(iw-sw)/2; }
      else{ sh=iw/boxRatio; sy=(ih-sh)/2; }
      try{ ctx.drawImage(img,sx,sy,sw,sh,0,0,canvas.width,canvas.height); }catch(e){}
    }
    function setIcon(playing){
      icon.innerHTML=playing
        ? '<path d="M0 0h3v12H0zM7 0h3v12H7z"/>'
        : '<path d="M0 0l10 6-10 6z"/>';
      icon.setAttribute("viewBox","0 0 10 12");
    }
    function freeze(){
      if(frozen || !img.getAttribute("src")) return;
      paint();                           // capture the exact frame on screen first
      canvas.dataset.frozen="true";
      img.removeAttribute("src");        // actually stops the GIF decoding, not just visually
      frozen=true;
      btn.setAttribute("aria-pressed","true");
      btn.setAttribute("aria-label","Play animation");
      setIcon(false);
    }
    function unfreeze(){
      if(!frozen) return;
      img.setAttribute("src",originalSrc);  // restarts at frame one
      canvas.dataset.frozen="false";
      frozen=false;
      btn.setAttribute("aria-pressed","false");
      btn.setAttribute("aria-label","Pause animation");
      setIcon(true);
    }
    btn.addEventListener("click",()=>{ frozen?unfreeze():freeze(); });
    teardown.push(press(btn,()=>{
      track(animate(btn,{scale:MO.scalePress},{duration:MO.instant,ease:MO.eStandard}));
      return ()=>track(animate(btn,{scale:1},reduced?{duration:MO.instant}:{type:"spring",stiffness:520,damping:16}));
    }));
    if(reduced) img.complete ? freeze() : img.addEventListener("load",freeze,{once:true});
  });
}

/* 6 · PRODUCTS */
const FAV="assets/figma/icon-favorite.svg";
const FAV_SOLID="assets/figma/icon-favorite-solid.svg";
function productCard(p){
  const el=document.createElement("a");
  el.className="pcard"; el.href=p.url; el.dataset.pcard="";
  if(/^https?:/.test(p.url)){ el.target="_blank"; el.rel="noopener"; } // live products open beside the lab; placeholders don't spawn a tab
  el.innerHTML=`
    <span class="pcard__media">
      <img class="pcard__img" src="${p.image}" alt="${p.name}" loading="lazy" decoding="async" data-pcard-img>
      <span class="pcard__fav" role="button" tabindex="0" aria-label="Save ${p.name}" aria-pressed="false" data-fav>
        <img src="${FAV}" alt="" width="30" height="30" data-fav-icon>
      </span>
    </span>
    <span class="pcard__info">
      <span class="pcard__name">${p.name}&mdash;${p.colorName}</span>
      <span class="pcard__price">${p.price}</span>
    </span>`;
  return el;
}
function initProducts(){
  $$("[data-products]").forEach(section=>{
    const rail=$("[data-pcard-rail]",section);
    if(!rail.childElementCount) PRODUCTS[section.dataset.products].forEach(p=>rail.appendChild(productCard(p)));
    const title=$("[data-products-title]",section), toggle=$(".gtoggle",section);
    const cards=$$("[data-pcard]",section);
    teardown.push(inView(section,()=>{
      tween([title,toggle],{y:[MO.riseSm,0],opacity:[0,1]},{duration:MO.slow,delay:stagger(MO.stagTight),ease:MO.eEntrance});
      /* stagger-tight — a product row has to stay scannable */
      tween(cards,{y:[MO.riseMd,0],opacity:[0,1]},{duration:MO.deliberate,delay:stagger(MO.stagTight,{startDelay:.08}),ease:MO.eEntrance});
    },{amount:.15}));
    cards.forEach(card=>{
      const img=$("[data-pcard-img]",card), fav=$("[data-fav]",card), favIcon=$("[data-fav-icon]",card);
      teardown.push(hover(card,()=>{
        track(animate(img,{scale:MO.scaleHover},{duration:MO.slow,ease:MO.eEntrance}));
        return ()=>track(animate(img,{scale:1},{duration:MO.slow,ease:MO.eEntrance}));
      }));
      const toggleFav=(e)=>{
        e.preventDefault(); e.stopPropagation();
        const on=fav.getAttribute("aria-pressed")!=="true";
        fav.setAttribute("aria-pressed",String(on));
        favIcon.src=on?FAV_SOLID:FAV;
        track(animate(fav,{scale:[MO.scalePress,1.18,1]},
          reduced?{duration:MO.instant}:{type:"spring",stiffness:520,damping:14}));
      };
      fav.addEventListener("click",toggleFav);
      fav.addEventListener("keydown",(e)=>{ if(e.key==="Enter"||e.key===" ") toggleFav(e); });
    });
  });
  /* gender toggles are presentational in this prototype */
  $$(".gtoggle").forEach(g=>{
    $$("button",g).forEach(b=>b.addEventListener("click",()=>{
      $$("button",g).forEach(o=>o.setAttribute("aria-pressed","false"));
      b.setAttribute("aria-pressed","true");
      track(animate(b,{scale:[.96,1]},reduced?{duration:MO.instant}:{duration:MO.base,ease:MO.eEntrance}));
    }));
  });
}

/* ============================================================
   7 · COLOR LAB — click (desktop) + swipe (mobile)
   Built for smoothness: the DOM is created once and only ever
   updated in place. Nothing is torn down and rebuilt per
   colour change, which is what produced the earlier flicker.
   ============================================================ */
let labGender = "women";           // "women" | "men" — driven by the Color Lab toggle
let COLORS = WOMEN_COLORS;         // the active gender's colour list — swapped wholesale in setGender()
let labIndex = 0;                  // Black, matching the Figma default (index 0 in WOMEN_COLORS)
const labWrap = (i) => (i % COLORS.length + COLORS.length) % COLORS.length;
const labSrc  = (c) => matchMedia("(min-width:768px)").matches ? c.full : c.sm;
const labThumb= (c) => c.thumb;

function initColorLab(){
  const root=$("[data-lab]");
  const strip=$("[data-lab-strip]");
  const layers=[$('[data-lab-layer="0"]'),$('[data-lab-layer="1"]')];
  const nameEl=$("[data-lab-name]"), catEl=$("[data-lab-cat]");
  const swatchBox=$("[data-lab-swatches]");
  const railBox=$("[data-lab-rail]");
  const tip=$("[data-lab-tip]"), tipName=$("[data-lab-tip-name]"), tipCat=$("[data-lab-tip-cat]");
  const chipEl=$("[data-lab-chip]");
  const dots=$$("[data-lab-dot]");
  const stage=$("[data-lab-stage]"), activeSlot=$("[data-lab-active]"), hotWrap=$("[data-lab-hotspots]");
  const hotspots=[
    ["top",  $('[data-lab-hot="top"]'),  $('[data-lab-hotcard="top"]')],
    ["pant", $('[data-lab-hot="pant"]'), $('[data-lab-hotcard="pant"]')],
  ];
  let front=0;                    // which layer is currently visible
  let dotsShown=false;
  let openHot=null, hotHideT=null;

  $$("[data-lab-count]").forEach(n=>n.textContent=String(COLORS.length));

  /* ---------- swatch row: rebuilt (not just synced) on every gender
     switch, since WOMEN_COLORS/MEN_COLORS differ in both length and
     content — clearing first makes repeat calls (initial boot, then any
     later gender switch) equally safe. ---------- */
  function buildSwatches(){
    /* clear only the swatch buttons — swatchBox also hosts the .lab__tip
       tooltip as a sibling, which a blanket innerHTML="" would delete */
    $$("[data-lab-swatch]",swatchBox).forEach(b=>b.remove());
    COLORS.forEach((c,i)=>{
      const b=document.createElement("button");
      b.className="swatch"; b.type="button"; b.dataset.labSwatch=String(i);
      b.style.setProperty("--sw",c.hex);
      if(c.hex==="#ffffff") b.dataset.white="";
      b.setAttribute("aria-label",`${c.name} — ${c.cat}`);
      b.setAttribute("aria-pressed",String(i===labIndex));
      b.innerHTML="<i></i>";
      /* the page's one-time scroll-reveal (.mo-pre) pre-hides every
         [data-lab-swatch] via CSS and only the FIRST batch ever gets
         un-hidden, by the entrance tween further down setting an inline
         opacity. A gender switch rebuilds this row long after that
         one-time reveal has already fired, so fresh buttons need their
         own explicit opacity or they're born invisible and stay that
         way — the entrance tween below still overrides this on the
         very first build, so the initial wipe-in animation is unaffected. */
      b.style.opacity="1";
      swatchBox.appendChild(b);
    });
  }
  buildSwatches();

  if(!railBox.childElementCount){
    for(let s=0;s<7;s++){
      if(s===3){
        const row=document.createElement("div"); row.className="lab__rail-active";
        /* Figma 169:17221 — the two arrows stack (40×80) to the LEFT of the
           active thumb (50 wide), with a 20px gap between them. */
        row.innerHTML=
          `<div class="lab__arrows">
             <button type="button" data-lab-step="-1" aria-label="Previous colour">
               <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 8l6 8H6z"/></svg></button>
             <button type="button" data-lab-step="1" aria-label="Next colour">
               <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 16l-6-8h12z"/></svg></button>
           </div>
           <button type="button" class="lab__thumb" data-lab-slot="3" aria-pressed="true"><img alt="" decoding="async"></button>`;
        railBox.appendChild(row);
      }else{
        const t=document.createElement("button");
        t.type="button"; t.className="lab__thumb"; t.dataset.labSlot=String(s);
        t.innerHTML=`<img alt="" loading="lazy" decoding="async">`;
        railBox.appendChild(t);
      }
    }
  }

  /* ---------- in-place sync (no DOM churn) ---------- */
  function setSrc(img,src){ if(img.getAttribute("src")!==src) img.src=src; }

  /* A few of Leon's photos were shot left of centre (Black, Burgundy), and a
     cover crop trims both sides equally, so it kept him there: shift each
     men's image so his measured centre (c.cx) lands mid-box. Contained
     images (the women's PNGs) and centred photos keep object-position. */
  function centre(img){
    const cx=img._c?.cx;
    if(cx==null || !img.naturalWidth || !img.clientWidth){ img.style.objectPosition=""; return; }
    const bw=img.clientWidth, bh=img.clientHeight;
    const dw=img.naturalWidth*Math.max(bw/img.naturalWidth, bh/img.naturalHeight), ox=dw-bw;
    img.style.objectPosition = ox>1 ? `${(Math.max(0,Math.min(1,(cx*dw-bw/2)/ox))*100).toFixed(2)}% 50%` : "";
  }
  /* any model image: its colour's fit goes on with the src, the shift once
     the photo's size is known */
  function show(img,c,src){
    img._c=c; img.dataset.fit=c.fit||"";
    setSrc(img,src);
    if(img.complete && img.naturalWidth) centre(img);
    else img.addEventListener("load",()=>centre(img),{once:true});
  }
  /* where an image actually lands in its box — contained, or cover-cropped
     and shifted — for placing hotspots on it */
  function drawnRect(img){
    const bw=img.clientWidth, bh=img.clientHeight, nw=img.naturalWidth, nh=img.naturalHeight;
    const cs=getComputedStyle(img);
    const s=(cs.objectFit==="contain"?Math.min:Math.max)(bw/nw, bh/nh), dw=nw*s, dh=nh*s;
    const [px,py]=cs.objectPosition.split(" ").map(v=>parseFloat(v)/100);
    return { x:(bw-dw)*px, y:(bh-dh)*py, w:dw, h:dh };
  }

  function syncRail(){
    $$("[data-lab-slot]",railBox).forEach(btn=>{
      const off=Number(btn.dataset.labSlot)-3;
      const c=COLORS[labWrap(labIndex+off)];
      show($("img",btn), c, labThumb(c));
      btn.setAttribute("aria-label", off===0 ? `${c.name} — current` : c.name);
      btn.dataset.labSwatch = off===0 ? "" : String(labWrap(labIndex+off));
      if(off===0) btn.removeAttribute("data-lab-swatch");
    });
  }
  function syncSides(){
    $$("[data-lab-side]",strip).forEach(slot=>{
      const off=Number(slot.dataset.labSide);
      const c=COLORS[labWrap(labIndex+off)];
      show($("img",slot), c, labThumb(c));
      slot.dataset.labSwatch=String(labWrap(labIndex+off));
    });
  }
  function syncSwatches(){
    $$("[data-lab-swatch]",swatchBox).forEach(b=>
      b.setAttribute("aria-pressed",String(Number(b.dataset.labSwatch)===labIndex)));
  }
  function setText(c){
    nameEl.textContent=c.name; catEl.textContent=c.cat;
    if(chipEl) chipEl.style.background=c.hex;
    /* SHOP NOW goes where the colour is in the lab: Women's — Charcoal's
       PLP, else the Catarina PDP in it (the nav's labColorHref); Men's —
       the Men's Core PLP */
    const shop = labGender === "men" ? "/experiments/12-plp-core/" : labColorHref(c.name);
    document.querySelectorAll("[data-lab-shop]").forEach((a)=>{ a.href = shop || "#"; });
  }
  /* keep the selected chip in view in the mobile swatch strip */
  function revealSwatch(instant){
    const btn=$(`[data-lab-swatch="${labIndex}"]`,swatchBox);
    if(!btn || swatchBox.scrollWidth<=swatchBox.clientWidth+1) return;
    const target=btn.offsetLeft-(swatchBox.clientWidth-btn.offsetWidth)/2;
    swatchBox.scrollTo({left:Math.max(0,target), behavior:(instant||reduced)?"auto":"smooth"});
  }

  function preload(){
    [-2,-1,1,2].forEach(o=>{ const im=new Image(); im.src=labSrc(COLORS[labWrap(labIndex+o)]); });
  }

  /* ---------- model crossfade between two stacked layers ---------- */
  async function swapModel(c,dir){
    const back=layers[1-front], fore=layers[front];
    const src=labSrc(c);
    back._c=c; back.dataset.fit=c.fit||"";
    back.src=src; back.alt=`Model wearing FIGS scrubs in ${c.name}`;
    try{ await back.decode(); }catch(e){}          // never fade in a blank frame
    centre(back);

    if(reduced){
      back.style.opacity="1"; fore.style.opacity="0";
      front=1-front; positionHotspots(); return;
    }
    track(animate(back,{opacity:[0,1],x:[dir*14,0]},{duration:MO.slow,ease:MO.eEntrance}));
    track(animate(fore,{opacity:[1,0],x:[0,dir*-10]},{duration:MO.base,ease:MO.eStandard}));
    front=1-front; positionHotspots();
  }

  /* ---------- the one public entry point ---------- */
  function apply(next,dir){
    const target=labWrap(next);
    if(target===labIndex) return;
    const d = dir ?? (target>labIndex?1:-1);
    labIndex=target;
    const c=COLORS[labIndex];
    setText(c);                                    // text updates immediately — bug fix
    syncSwatches(); syncRail(); syncSides(); revealSwatch();
    swapModel(c,d); preload();
  }

  /* ---------- first paint (no animation) ---------- */
  (function first(){
    const c=COLORS[labIndex];
    layers[0]._c=c; layers[0].dataset.fit=c.fit||"";
    layers[0].src=labSrc(c); layers[0].alt=`Model wearing FIGS scrubs in ${c.name}`;
    layers[0].style.opacity="1"; layers[1].style.opacity="0";
    setText(c); syncSwatches(); syncRail(); syncSides(); preload(); revealSwatch(true);
    const ready=()=>{ centre(layers[0]); positionHotspots(); };
    if(layers[0].complete) ready();
    else layers[0].addEventListener("load",ready,{once:true});
  })();

  /* ---------- shop-the-look hotspots ---------- */
  function paintHotcards(){
    hotspots.forEach(([key,dot,card])=>{
      const p=LOOK[labGender][key];
      /* women's single-SKU jumpsuit only defines `top` — hide the `pant`
         dot+card entirely rather than leave a second hotspot pointing at
         nothing (or duplicating the same product). Inline display, not
         [hidden], since it has to beat .lab__hotcard's own display:flex. */
      dot.style.display = p ? "" : "none";
      card.style.display = p ? "" : "none";
      if(!p) return;
      card.href=p.url;
      const img=$(".lab__hotcard__img",card);
      img.src=p.image; img.alt=p.name;
      $(".lab__hotcard__kicker",card).textContent=p.kicker;
      $("[data-hc-name]",card).innerHTML=p.name.replace(/™/g,'<span class="tm">™</span>');
      $("[data-hc-price]",card).textContent=p.price;
      dot.setAttribute("aria-label",`${p.kicker} — ${p.name}`);
    });
  }
  paintHotcards();

  /* ---------- gender (women | men) ----------
     Swaps the whole model set + the shop-the-look products, keeping the
     current colour. Both lab toggles (desktop + mobile) stay in sync. */
  const genderBtns=$$(".gtoggle button[data-gender]",root);
  const genderOf=(b)=> b.textContent.trim().toLowerCase()==="men" ? "men" : "women";
  function setGender(g){
    if(g===labGender) return;
    const prevName=COLORS[labIndex]?.name;
    labGender=g;
    root.setAttribute("data-lab-gender",g);
    COLORS = g==="women" ? WOMEN_COLORS : MEN_COLORS;
    /* stay on the same named colour across the switch where it exists in
       the new list (e.g. both keep "Black"); otherwise fall back to 0 —
       the two lists differ in length/content so the old numeric index
       can't just carry over. */
    const matched=COLORS.findIndex(c=>c.name===prevName);
    labIndex = matched>=0 ? matched : 0;
    genderBtns.forEach(b=>b.setAttribute("aria-pressed",String(genderOf(b)===g)));
    $$("[data-lab-count]").forEach(n=>n.textContent=String(COLORS.length));
    buildSwatches(); wireSwatchHovers();
    const c=COLORS[labIndex];
    setText(c); syncSwatches(); syncRail(); syncSides(); revealSwatch(true);
    swapModel(c,1);                     // crossfade to the matched colour, new set
    preload(); paintHotcards();
  }
  genderBtns.forEach(b=>b.addEventListener("click",()=>setGender(genderOf(b))));

  function placeDotAndCard(dot,card,dx,dy,gr){
    dot.style.left=dx+"px"; dot.style.top=dy+"px";
    const cw=card.offsetWidth||220, ch=card.offsetHeight||96;
    let cl=Math.max(6, Math.min(dx-cw/2, gr.width-cw-6));
    let ct=dy-ch-16;                   // prefer above the dot
    if(ct<0) ct=dy+18;                 // flip below if it would clip the top
    card.style.left=cl+"px"; card.style.top=ct+"px";
  }
  function positionHotspots(){
    const layer=layers[front];
    if(!layer || !layer.naturalWidth) return;
    const sr=activeSlot.getBoundingClientRect(), gr=stage.getBoundingClientRect();
    if(!sr.width || !gr.width) return;
    /* `at` / `hot` are fractions of the photo, so they're placed through
       where the photo actually lands in the box (drawnRect): the women's
       PNG contained, the men's square photo cover-cropped and centred */
    const r=drawnRect(layer), ox=sr.left-gr.left, oy=sr.top-gr.top, c=layer._c||COLORS[labIndex];
    const dx=(c.cx??.5)-.5;          // the men's `at` was set on a centred figure
    hotspots.forEach(([key,dot,card])=>{
      const p=LOOK[labGender][key];
      if(!p) return;                 // e.g. women's "pant" — no second SKU to place
      const at=(key==="top" && c.hot) || p.at;
      placeDotAndCard(dot,card, ox+r.x+(at.x+dx)*r.w, oy+r.y+at.y*r.h, gr);
    });
  }

  function openCard(key){
    hotspots.forEach(([k,dot,card])=>{
      const on=(k===key);
      dot.setAttribute("aria-expanded",String(on));
      card.setAttribute("aria-hidden",String(!on));
      card.tabIndex=on?0:-1;
      if(on) track(animate(card,{opacity:[0,1],y:[6,0],scale:[.97,1]},
        reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eEntrance}));
      else card.style.opacity="0";
    });
    openHot=key;
    positionHotspots();              // card now has real dimensions
  }
  function closeCards(){
    if(openHot===null) return; openHot=null;
    hotspots.forEach(([k,dot,card])=>{
      dot.setAttribute("aria-expanded","false");
      card.setAttribute("aria-hidden","true"); card.tabIndex=-1;
      track(animate(card,{opacity:0},reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eStandard}));
    });
  }
  const cancelHide=()=>{ if(hotHideT){clearTimeout(hotHideT); hotHideT=null;} };
  const scheduleHide=()=>{ cancelHide(); hotHideT=setTimeout(closeCards,140); };
  hotspots.forEach(([key,dot,card])=>{
    dot.addEventListener("pointerenter",(e)=>{ if(e.pointerType==="touch") return; cancelHide(); openCard(key); });
    dot.addEventListener("pointerleave",(e)=>{ if(e.pointerType==="touch") return; scheduleHide(); });
    card.addEventListener("pointerenter",cancelHide);
    card.addEventListener("pointerleave",scheduleHide);
    dot.addEventListener("click",(e)=>{ e.preventDefault(); e.stopPropagation(); cancelHide(); (openHot===key)?closeCards():openCard(key); });
    dot.addEventListener("focus",()=>{ cancelHide(); openCard(key); });
    dot.addEventListener("keydown",(e)=>{ if(e.key==="Escape"){ closeCards(); dot.blur(); } });
  });
  document.addEventListener("click",(e)=>{ if(!e.target.closest("[data-lab-hotspots]")) closeCards(); });
  addEventListener("resize",()=>requestAnimationFrame(()=>{
    [...layers, ...$$("img",railBox), ...$$("[data-lab-side] img",strip)].forEach(centre);
    positionHotspots();
  }),{passive:true});

  /* ---------- click ---------- */
  root.addEventListener("click",(e)=>{
    const sw=e.target.closest("[data-lab-swatch]");
    if(sw && sw.dataset.labSwatch!==""){ apply(Number(sw.dataset.labSwatch)); return; }
    const st=e.target.closest("[data-lab-step]");
    if(st){ apply(labIndex+Number(st.dataset.labStep), Number(st.dataset.labStep)); }
  });

  /* ---------- swatch hover: enlargement + tooltip ---------- */
  let tipOn=false;
  function showTip(btn){
    const c=COLORS[Number(btn.dataset.labSwatch)];
    tipName.textContent=c.name; tipCat.textContent=c.cat;
    tip.setAttribute("aria-hidden","false");
    const bx=btn.getBoundingClientRect(), wx=swatchBox.getBoundingClientRect();
    const centre=bx.left-wx.left+bx.width/2;
    /* keep the bubble inside the row, but point the arrow at the swatch */
    const half=tip.offsetWidth/2;
    const left=Math.max(0,Math.min(centre-half, wx.width-tip.offsetWidth));
    tip.style.left=`${left}px`;
    tip.style.setProperty("--tip-arrow",`${centre-left}px`);
    if(!tipOn){
      tipOn=true;
      track(animate(tip,{opacity:[0,1],y:[6,0],scale:[.96,1]},
        reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eEntrance}));
    }
  }
  function hideTip(){
    if(!tipOn) return; tipOn=false;
    tip.setAttribute("aria-hidden","true");
    track(animate(tip,{opacity:0,y:4},reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eStandard}));
  }
  /* re-run after buildSwatches() rebuilds the row (gender switch), since
     fresh buttons carry none of the previous listeners */
  function wireSwatchHovers(){
    $$("[data-lab-swatch]",swatchBox).forEach(btn=>{
      const chip=$("i",btn);
      /* +2px on every side, same as the active state (and the CSS :hover
         rule it overrides here via inline style) — not an arbitrary 1.28x */
      teardown.push(hover(btn,()=>{
        showTip(btn);
        track(animate(chip,{scale:1.1111},reduced?{duration:MO.instant}:{type:"spring",stiffness:620,damping:26}));
        return ()=>{
          hideTip();
          track(animate(chip,{scale:1},reduced?{duration:MO.instant}:{duration:MO.quick,ease:MO.eEntrance}));
        };
      }));
      btn.addEventListener("focus",()=>{ showTip(btn); track(animate(chip,{scale:1.1111},{duration:MO.quick,ease:MO.eEntrance})); });
      btn.addEventListener("blur", ()=>{ hideTip(); track(animate(chip,{scale:1},{duration:MO.quick,ease:MO.eEntrance})); });
    });
  }
  wireSwatchHovers();
  swatchBox.addEventListener("pointerleave",hideTip);

  /* ---------- keyboard ---------- */
  strip.tabIndex=0;
  strip.addEventListener("keydown",(e)=>{
    if(e.key==="ArrowRight"||e.key==="ArrowDown"){ e.preventDefault(); apply(labIndex+1,1); }
    if(e.key==="ArrowLeft" ||e.key==="ArrowUp"  ){ e.preventDefault(); apply(labIndex-1,-1); }
  });

  /* ---------- swipe (mobile) ---------- */
  let x0=null,y0=null,locked=null,dragging=false;
  const THRESH=42;
  strip.addEventListener("pointerdown",(e)=>{
    if(mqDesktop.matches) return;
    x0=e.clientX; y0=e.clientY; locked=null; dragging=true;
  });
  strip.addEventListener("pointermove",(e)=>{
    if(!dragging||x0===null) return;
    const dx=e.clientX-x0, dy=e.clientY-y0;
    if(locked===null && (Math.abs(dx)>10||Math.abs(dy)>10))
      locked = Math.abs(dx)>Math.abs(dy) ? "x" : "y";
    if(locked==="x"){
      e.preventDefault();
      layers[front].style.transform=`translate3d(${Math.sign(dx)*Math.min(Math.abs(dx)*.45,44)}px,0,0)`;
    }
  },{passive:false});
  const endDrag=(e)=>{
    if(!dragging) return; dragging=false;
    const dx=(e.clientX??x0)-x0;
    const f=layers[front];
    if(locked==="x" && Math.abs(dx)>THRESH){
      f.style.transform="";
      apply(labIndex+(dx<0?1:-1), dx<0?1:-1);
    }else if(locked==="x"){
      /* under threshold — spring back rather than snapping */
      track(animate(f,{x:0},reduced?{duration:MO.instant}:{type:"spring",stiffness:420,damping:30}));
    }
    x0=y0=null; locked=null;
  };
  strip.addEventListener("pointerup",endDrag);
  strip.addEventListener("pointercancel",endDrag);

  /* ---------- entrance (runs once per boot, not per colour) ---------- */
  teardown.push(inView(root,()=>{
    tween($$(".lab__col-copy > *"),{y:[MO.riseSm,0],opacity:[0,1]},
      {duration:MO.slow,delay:stagger(MO.stagBase),ease:MO.eEntrance});
    tween($("[data-lab-active]"),{y:[MO.riseMd,0],opacity:[0,1]},
      {duration:MO.cinematic,delay:.1,ease:MO.eEntrance});
    tween($$("[data-lab-side]"),{opacity:[0,.85],y:[MO.riseSm,0]},
      {duration:MO.deliberate,delay:stagger(MO.stagBase,{startDelay:.2}),ease:MO.eEntrance});
    /* swatches wipe in as a row — opacity only, no scaleY snap */
    tween($$("[data-lab-swatch]"),{opacity:[0,1]},
      {duration:MO.base,delay:stagger(.012,{startDelay:.3}),ease:MO.eEntrance});
    hotWrap.setAttribute("aria-hidden","false");
    positionHotspots();
    if(!dotsShown){
      dotsShown=true;
      tween(dots,{opacity:[0,1],scale:[.4,1]},
        {duration:MO.base,delay:stagger(MO.stagBase,{startDelay:.8}),ease:MO.eEntrance});
    }else{
      dots.forEach(d=>d.style.opacity="1");
    }
  },{amount:.2}));
}

/* 8 · FOOTER */
function initFooter(){
  teardown.push(inView($("[data-footer]"),()=>{
    tween($$("[data-footer-item]"),{y:[MO.riseMd,0],opacity:[0,1]},
      {duration:MO.deliberate,delay:stagger(MO.stagBase),ease:MO.eEntrance});
  },{amount:.1}));

  /* mobile accordion — desktop (≥1024) forces every list open via CSS and
     makes the header inert, so this click handler is a no-op there. Not
     guarded against Replay re-init: it only toggles state, never binds
     anything that would double up if bound twice... except it does add
     one listener per boot(), so guard it with a one-time data flag. */
  const PLUS='<path d="M5 1v8M1 5h8"/>', MINUS='<path d="M1 5h8"/>';
  $$("[data-fgroup]").forEach(group=>{
    if(group.dataset.fgroupInit) return;
    group.dataset.fgroupInit="1";
    const btn=$("[data-fgroup-toggle]",group), list=$("[data-fgroup-list]",group), icon=$("[data-fgroup-icon]",group);
    btn.addEventListener("click",()=>{
      if(matchMedia("(min-width:1024px)").matches) return;   // desktop: always open, header inert
      const open=btn.getAttribute("aria-expanded")==="true";
      btn.setAttribute("aria-expanded",String(!open));
      list.hidden=open;
      icon.innerHTML=open?PLUS:MINUS;
    });
  });
}

/* 9 · SHARED MICRO-INTERACTIONS */
function initMicro(){
  $$("[data-hover-btn]").forEach(b=>{
    teardown.push(hover(b,()=>{
      track(animate(b,{scale:MO.scaleHover},{duration:MO.quick,ease:MO.eEntrance}));
      return ()=>track(animate(b,{scale:1},{duration:MO.quick,ease:MO.eEntrance}));
    }));
    teardown.push(press(b,()=>{
      track(animate(b,{scale:MO.scalePress},{duration:MO.instant,ease:MO.eStandard}));
      return ()=>track(animate(b,{scale:1},reduced?{duration:MO.instant}:{type:"spring",stiffness:480,damping:18}));
    }));
  });
  $$("[data-hover-link]").forEach(l=>{
    teardown.push(hover(l,()=>{
      track(animate(l,{y:-2,opacity:.82},{duration:MO.quick,ease:MO.eEntrance}));
      return ()=>track(animate(l,{y:0,opacity:1},{duration:MO.quick,ease:MO.eEntrance}));
    }));
  });
  $$("[data-press]").forEach(el=>{
    teardown.push(press(el,()=>{
      track(animate(el,{scale:MO.scalePress},{duration:MO.instant,ease:MO.eStandard}));
      return ()=>track(animate(el,{scale:1},reduced?{duration:MO.instant}:{type:"spring",stiffness:520,damping:16}));
    }));
  });
  /* video controls live in initVideo() — they need the real element */
}

/* LAB BAR */
function initLabBar(){
  const bar=$("[data-lab-bar]"), handle=$("[data-lab-handle]");
  const slider=$("#lab-speed"), readout=$("[data-readout]");
  slider.addEventListener("input",()=>{
    speed=parseFloat(slider.value); readout.textContent=speed.toFixed(2)+"x";
    tracked.forEach(c=>{ try{ c.speed=speed; }catch(e){} });
  });
  const rebuild=()=>{
    window.scrollTo({top:0,behavior:"instant"});
    teardown.splice(0).forEach(fn=>{ try{ fn?.(); }catch(e){} });
    tracked.clear(); requestAnimationFrame(boot);
  };
  $("[data-replay]").addEventListener("click",rebuild);
  const rb=$("[data-reduced-btn]");
  rb.addEventListener("click",()=>{
    reduced=!reduced; rb.setAttribute("aria-pressed",String(reduced));
    document.documentElement.dataset.reduced=String(reduced); rebuild();
  });
  const gb=$("[data-grid-btn]"), ov=$("[data-grid-overlay]");
  gb.addEventListener("click",()=>{
    const on=gb.getAttribute("aria-pressed")!=="true";
    gb.setAttribute("aria-pressed",String(on)); ov.dataset.on=String(on);
  });
  /* Hide removes the toolbar for the rest of the session — unlike the H key,
     which just toggles it behind the restore handle. */
  let killed=false;
  $("[data-hide-btn]").addEventListener("click",()=>{
    killed=true; handle.hidden=true; bar.remove();
  });
  addEventListener("keydown",(e)=>{
    if(killed) return;
    if(e.key.toLowerCase()!=="h"||e.metaKey||e.ctrlKey) return;
    if(/^(input|textarea|select)$/i.test(e.target.tagName)) return;
    const hide=!bar.hidden; bar.hidden=hide; handle.hidden=!hide;
  });
  handle.addEventListener("click",()=>{ if(killed) return; bar.hidden=false; handle.hidden=true; });
  tween(bar,{y:[MO.riseMd,0],opacity:[0,1]},{duration:MO.slow,delay:.4,ease:MO.eEntrance});
}

function boot(){
  document.documentElement.dataset.reduced=String(reduced);
  initHero(); initVideo(); initCollections(); initBanner(); initSplit(); initGifControls();
  initProducts(); initColorLab(); initFooter(); initMicro();
}
boot();
/* tells the head safety-net the reveals are running, so .mo-pre stays put
   for the sections that haven't scrolled into view yet */
document.documentElement.setAttribute("data-mo-ready","");
initLabBar();

/* swap the model resolution when the breakpoint changes, on the
   visible layer only — silently, so it never reads as a flash */
matchMedia("(min-width:768px)").addEventListener("change",()=>{
  const c=COLORS[labIndex], want=labSrc(c);
  $$("[data-lab-layer]").forEach(l=>{
    if(getComputedStyle(l).opacity==="1" && l.getAttribute("src")!==want) l.src=want;
  });
});

addEventListener("load",()=>{
  /* only images that finished without pixels: a lazy image that hasn't been
     asked for yet (hidden, or below the fold) and an empty placeholder
     aren't failures */
  const broken=$$("img").filter(i=>i.getAttribute("src")&&i.complete&&i.naturalWidth===0);
  if(broken.length) console.error(`[assets] ${broken.length} image(s) failed to load — Figma CDN URLs may have expired. Run ./vendor-assets.sh.`, broken.map(i=>i.src));
  else console.info(`[assets] ${$$("img").length} images OK`);
});
console.info("%cFIGS Motion Lab · 08 MVP Home + Color Lab","font-weight:700",
  "\nMotion: motion.dev v12 (vanilla)","\nColours:",COLORS.length,"\nReduced:",reduced);
